import { createContext } from "react";

import EmailDraft from "@mapmycustomers/shared/types/draft/EmailDraft";
import EmailAssociatedEntity from "@mapmycustomers/shared/types/email/EmailAssociatedEntity";
import EmailDynamicVariable from "@mapmycustomers/shared/types/email/EmailDynamicVariable";
import EmailEntitiesMissedFields from "@mapmycustomers/shared/types/email/EmailEntitiesMissedFields";
import EmailPreview from "@mapmycustomers/shared/types/email/EmailPreview";
import EmailTemplate from "@mapmycustomers/shared/types/email/EmailTemplate";
import { Deal } from "@mapmycustomers/shared/types/entity";
import {
  EntitiesSupportedByEmailFeature,
  EntityTypesSupportedByEmailFeature,
} from "@mapmycustomers/shared/types/entity";
import Resource from "@mapmycustomers/shared/types/entity/Resource";
import { EmailAttachedFile, EmailBodyEditor, EmailBodyProps } from "@mapmycustomers/ui";

import { SendEmailPayload } from "@app/store/api/ApiService";

export interface EmailContextValueState {
  aiSubjectSuggestion?: string;
  aiWriterVisible: boolean;
  associatedEntities: EmailAssociatedEntity[];
  attachedFiles: EmailAttachedFile[];
  attachedFilesTouched: boolean;
  body: string;
  bodyTouched: boolean;
  deals: Deal[];
  draftId?: EmailDraft["id"];
  draftSaving: boolean;
  dynamicVariablesSupported: boolean;
  editor?: EmailBodyEditor;
  entities: EntitiesSupportedByEmailFeature[];
  entityType: EntityTypesSupportedByEmailFeature;
  preview?: EmailPreview;
  previewLoading: boolean;
  previewOpen: boolean;
  previewRecipientId?: EntitiesSupportedByEmailFeature["id"];
  sending: boolean;
  sent: boolean;
  subject: string;
  subjectTouched: boolean;
  template?: EmailTemplate;
  templateChanged: boolean;
  unsubscribeLinkIncluded?: boolean;
}

interface EmailContextValueActions {
  onAiSubjectSuggestionChange(value: string | undefined): void;
  onAiWriterToggle(): void;
  onAttachedFileDelete(attachedFile: EmailAttachedFile): void;
  onDraftChange(draftId: EmailDraft["id"] | undefined): void;
  onDraftCreated(draftId: EmailDraft["id"]): void;
  onDraftSavingChange(value: boolean): void;
  onEntitiesChange(value: EntitiesSupportedByEmailFeature[]): void;
  onEntityTypeChange(value: EntityTypesSupportedByEmailFeature): void;
  onPreviewCreate(email: SendEmailPayload): void;
  onPreviewHide(): void;
  onPreviewRecipientIdChange: (value?: EntitiesSupportedByEmailFeature["id"]) => void;
  onPreviewShow(): void;
  onSendingChange(value: boolean): void;
  onSent: () => void;
  onSubjectChange: (value: EmailContextValueState["subject"]) => void;
  onTemplateChange(value: EmailContextValueState["template"]): void;
  onUnsubscribeLinkIncludedChange(value: EmailContextValueState["unsubscribeLinkIncluded"]): void;
}

interface EmailContextValueSelectors {
  attachedFilesSizeLimitReached: boolean;
  bodyContainsDynamicVariables: boolean;
  bodyLimitReached: boolean;
  composedBody: string;
  dynamicVariables: EmailDynamicVariable[];
  emailFilled: boolean;
  emailTouched: boolean;
  entitiesMissedFields: EmailEntitiesMissedFields;
  loading: boolean;
  subjectContainsDynamicVariables: boolean;
  validationError: string | undefined;
}

export type EmailBodyContextValue = Required<
  Pick<EmailBodyProps, "onChange" | "onFileAttach" | "onImageUpload" | "onInit">
>;

export type EmailResourcesContextValue = {
  resources: ReadonlyMap<Resource["id"], Resource>;
} & Required<
  Pick<
    EmailBodyProps,
    "onResourceDownload" | "onResourceLinkInsert" | "onResourceLinksChange" | "resourceGetter"
  >
>;

export type EmailContextValue = EmailContextValueState &
  EmailContextValueActions &
  EmailBodyContextValue &
  EmailContextValueSelectors &
  EmailResourcesContextValue;

const EmailContext = createContext<EmailContextValue | null>(null);

EmailContext.displayName = "EmailContext";

export default EmailContext;
